import type { ReactNode } from 'react';
import { Button, Finding, Section, Status } from '../ui.tsx';
import { t } from '../i18n/index.ts';
import { currentStep, type OnboardingStep } from '../lib/onboarding.ts';

/**
 * The first-run wizard for the Evidence module: enable → ingest → verify.
 *
 * It is a guide, not a second implementation. Enabling calls the real org
 * toggle; ingesting opens the real browser-capture flow; verifying opens a real
 * held asset and points at the bundle the offline verifier reads. The wizard
 * only sequences those and says, honestly, what each one does and does not do.
 *
 * Reachable, skippable, resumable: the parent mounts it whenever setup is
 * incomplete and not dismissed, and offers a quiet "Setup guide" link to bring
 * it back. Its step is a pure function of live progress, so a reload resumes
 * exactly where the data actually is.
 *
 * Every status is a word and a dot, never colour alone — the greyscale rule the
 * whole dashboard is held to.
 */

const STEP_ORDER: Exclude<OnboardingStep, 'done'>[] = ['enable', 'ingest', 'verify'];

function stepState(step: OnboardingStep, current: OnboardingStep): 'done' | 'now' | 'next' {
  if (current === 'done') return 'done';
  const order = ['enable', 'ingest', 'verify', 'done'];
  const si = order.indexOf(step);
  const ci = order.indexOf(current);
  if (si < ci) return 'done';
  if (si === ci) return 'now';
  return 'next';
}

function StepStatus({ state }: { state: 'done' | 'now' | 'next' }) {
  if (state === 'done') return <Status tone="safe">{t('onboarding.status.done')}</Status>;
  if (state === 'now') return <Status tone="attention">{t('onboarding.status.now')}</Status>;
  return <Status tone="neutral">{t('onboarding.status.next')}</Status>;
}

function Step({
  n,
  title,
  state,
  children,
}: {
  n: number;
  title: string;
  state: 'done' | 'now' | 'next';
  children: ReactNode;
}) {
  return (
    <li className="border-b border-[var(--line)] py-4 last:border-0">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-[14px] leading-[19px] font-[650]">
          <span className="mr-2 text-[var(--ink-3)] tabular">{n}</span>
          {title}
        </h3>
        <StepStatus state={state} />
      </div>
      <div className="mt-2 max-w-prose text-[14px] text-[var(--ink-2)]">{children}</div>
    </li>
  );
}

export function EvidenceOnboarding({
  enabled,
  hasAsset,
  isAdmin,
  enabling,
  toggleError,
  onEnable,
  onOpenCapture,
  onOpenLatest,
  demoLoaded,
  onLoadDemo,
  verifyAcked,
  onAckVerify,
  onSkip,
}: {
  enabled: boolean;
  hasAsset: boolean;
  isAdmin: boolean;
  enabling: boolean;
  toggleError: string;
  onEnable: () => void;
  onOpenCapture?: () => void;
  onOpenLatest?: () => void;
  demoLoaded: boolean;
  onLoadDemo: () => void;
  verifyAcked: boolean;
  onAckVerify: () => void;
  onSkip?: () => void;
}) {
  const current = currentStep({ enabled, hasAsset, verifyAcked });
  const done = current === 'done';

  return (
    <Section
      title={t('onboarding.title')}
      action={
        done || !onSkip ? null : (
          <Button variant="quiet" onClick={onSkip} label={t('onboarding.skip.label')}>
            {t('onboarding.skip')}
          </Button>
        )
      }
    >
      <p className="mb-2 max-w-prose text-[14px] text-[var(--ink-2)]">
        {done
          ? 'Evidence management is set up. Ingested evidence appears below with its chain of custody, and anyone you hand a verification bundle can check it without trusting Duress.'
          : 'Three steps turn this into a working evidence practice. You can leave at any point and pick up where the data actually is.'}
      </p>

      {done ? null : (
        <ol className="m-0 list-none p-0">
          {STEP_ORDER.map((step, i) => {
            const state = stepState(step, current);
            if (step === 'enable') {
              return (
                <Step key={step} n={i + 1} title={t('onboarding.step.enable')} state={state}>
                  <p className="m-0">
                    Adds the module for members with standing and lets you ingest evidence. It
                    stores nothing until you ingest something, and it changes nothing else about
                    your organisation.
                  </p>
                  {state === 'now' ? (
                    <div className="mt-3">
                      {toggleError ? <Finding tone="alert">{toggleError}</Finding> : null}
                      {isAdmin ? (
                        <Button variant="primary" disabled={enabling} onClick={onEnable}>
                          {enabling
                            ? t('onboarding.step.enable.busy')
                            : t('onboarding.step.enable.cta')}
                        </Button>
                      ) : (
                        <p className="m-0 text-[var(--ink-2)]">
                          An owner or admin of this organisation can turn it on.
                        </p>
                      )}
                    </div>
                  ) : null}
                </Step>
              );
            }
            if (step === 'ingest') {
              return (
                <Step key={step} n={i + 1} title={t('onboarding.step.ingest')} state={state}>
                  <p className="m-0">
                    Record something in the browser and ingest it into the evidence store. A browser
                    recording is graded <strong className="font-medium">declared</strong> — provable
                    from the moment it is ingested, and honestly never claimed as device-attested
                    capture.
                  </p>
                  {state === 'now' ? (
                    <div className="mt-3 flex flex-col gap-3">
                      <div className="flex flex-wrap gap-2">
                        {onOpenCapture ? (
                          <Button variant="primary" onClick={onOpenCapture}>
                            {t('onboarding.step.ingest.cta')}
                          </Button>
                        ) : null}
                        {!demoLoaded ? (
                          <Button variant="secondary" onClick={onLoadDemo}>
                            {t('onboarding.step.ingest.demo')}
                          </Button>
                        ) : null}
                      </div>
                      <p className="m-0 text-[13px] text-[var(--ink-3)]">
                        Sample data is a local illustration — clearly marked, and it never enters
                        the evidence store, so it does not complete this step. Ingest a real asset
                        to move on.
                      </p>
                    </div>
                  ) : null}
                </Step>
              );
            }
            return (
              <Step key={step} n={i + 1} title={t('onboarding.step.verify')} state={state}>
                <p className="m-0">
                  Anyone holding the files can check a committed asset without trusting Duress. Open
                  an asset and download its bundle — the signed manifest, its place in the public
                  transparency log, and the keys. It is the same bundle the offline verifier reads.
                </p>
                {state === 'now' ? (
                  <>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {onOpenLatest ? (
                        <Button variant="primary" onClick={onOpenLatest}>
                          {t('onboarding.step.verify.cta')}
                        </Button>
                      ) : null}
                      <Button variant="quiet" onClick={onAckVerify}>
                        {t('onboarding.step.verify.ack')}
                      </Button>
                    </div>
                    <p className="m-0 mt-3 text-[13px] text-[var(--ink-3)]">
                      This just marks the step complete — it is your acknowledgement, not a check
                      the dashboard ran. The real proof is the verdict the offline verifier gives
                      when it reads the bundle.
                    </p>
                  </>
                ) : null}
              </Step>
            );
          })}
        </ol>
      )}
    </Section>
  );
}
